import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { couponKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeaderNav from '@dashboard/components/CardHeaderNav';
import Spinner from '@dashboard/components/Spinner';
import DiscountsCouponsDetail from './components/DiscountsCouponsDetail';
import CardHeader from '@/dashboard/components/CardHeader';
import CardHeaderTitle from '@/dashboard/components/CardHeaderTitle';
import { formatPrice } from '@/dashboard/services/formatting';
import { useToast } from '@/dashboard/context/ToastContext';
import Button from '@/dashboard/components/Button';
import CouponEditDialog from './components/CouponEditDialog';
import { useAdminGhosting } from '@/dashboard/context/AdminGhostingContext/AdminGhostingContext';
import { confirm } from '@/dashboard/lib/confirm';
import { useHistory } from 'react-router';
import { isAfter } from 'date-fns';
import CouponStatusBadge from '@/dashboard/components/DiscountsCoupons/CouponStatusBadge';
import DiscountsCouponsPaymentsRedemptions from './components/DiscountsCouponsPaymentsRedemptions';
import DiscountsCouponsBookingsRedemptions from './components/DiscountsCouponsBookingsRedemptions';
import DiscountsCouponsSubscriptionsRedemptions from './components/DiscountsCouponsSubscriptionsRedemptions';

// https://github.com/checkout-page/checkoutpage-api/commit/6920e1c2dfc2f0c52c92a094ceb45864be460946
const handleRedemptionLimitsInCheckoutPageMergeData = '2025-10-04T06:09:43.000Z';

const getTabs = ({ coupon }) => [
  {
    name: 'Details',
    href: `/coupons/${coupon._id}`,
    render: props => <DiscountsCouponsDetail {...props} coupon={coupon} />,
  },
  {
    name: `One-time (${coupon.paymentsRedemptionCount})`,
    href: `/coupons/${coupon._id}/one-time-redemptions`,
    render: props => <DiscountsCouponsPaymentsRedemptions {...props} coupon={coupon} />,
  },
  {
    name: `Subscriptions (${coupon.subscriptionsRedemptionCount})`,
    href: `/coupons/${coupon._id}/subscriptions-redemptions`,
    render: props => <DiscountsCouponsSubscriptionsRedemptions {...props} coupon={coupon} />,
  },
  {
    name: `Bookings (${coupon.eventBookingsRedemptionCount})`,
    href: `/coupons/${coupon._id}/event-booking-redemptions`,
    render: props => <DiscountsCouponsBookingsRedemptions {...props} coupon={coupon} />,
  },
];

const DiscountsCouponsDetailOverview = () => {
  const { token, sellerId } = useAuth();
  const {
    params: { id: couponId },
  } = useRouteMatch();
  const { emitToast } = useToast();
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { setAdminGhostingSellerId } = useAdminGhosting();
  const history = useHistory();

  const {
    data: coupon,
    isError,
    isLoading,
  } = useQuery({
    queryKey: couponKey(couponId),
    queryFn: () => fetcher(`/sellers/${sellerId}/coupons/${couponId}`, token),
    enabled: !!couponId,
  });

  useEffect(() => {
    if (coupon?.seller) {
      setAdminGhostingSellerId(coupon.seller);
    }
  }, [coupon?.seller, setAdminGhostingSellerId]);

  async function deleteCoupon() {
    try {
      const result = await confirm('Delete this coupon?', {
        confirm: 'Delete',
      });

      if (!result) return;

      await fetcher(`/sellers/${sellerId}/coupons/${couponId}`, token, {
        method: 'DELETE',
      });

      queryClient.invalidateQueries({ queryKey: couponKey(couponId) });
      emitToast({
        title: 'Coupon deleted',
        status: 'success',
      });
      history.push('/coupons');
    } catch (err) {
      // await confirm is a promise and rejects the cancellation.
      if (err === false) return;

      emitToast({
        title: 'Unable to delete coupon',
        description: 'Please try again or contact support.',
        status: 'error',
      });
    }
  }

  if (isLoading) {
    return (
      <Card variant="root">
        <CardContent variant="root">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (!coupon || isError) {
    return (
      <Card variant="root">
        <CardContent variant="root">Something went wrong</CardContent>
      </Card>
    );
  }

  const tabs = getTabs({ coupon });

  return (
    <>
      <Card variant="root">
        <CardHeader variant="root">
          <div className="flex justify-between w-full">
            <div className="space-y-1">
              <CardHeaderTitle variant="root">{coupon.label}</CardHeaderTitle>

              <div className="flex flex-row gap-x-2 items-center">
                <p className="text-xl text-gray-900">
                  {coupon.amountOff
                    ? formatPrice({
                        amount: coupon.amountOff,
                        currency: coupon.currency,
                      })
                    : `${coupon.percentOff}%`}
                </p>

                <CouponStatusBadge coupon={coupon} />
              </div>
            </div>

            {!coupon.deleted && (
              <div className="flex items-start gap-2">
                <Button appearance="light" onClick={deleteCoupon}>
                  Delete coupon
                </Button>
                {isAfter(coupon.createdAt, handleRedemptionLimitsInCheckoutPageMergeData) && (
                  <Button appearance="primary" onClick={() => setEditDialogOpen(true)}>
                    Edit coupon
                  </Button>
                )}
              </div>
            )}
          </div>

          <CardHeaderNav.Container tabs={tabs}>
            {({ currentTab }) =>
              tabs.map(tab => (
                <CardHeaderNav.Tab key={tab.name} tab={tab} current={currentTab.href === tab.href}>
                  {tab.name}
                </CardHeaderNav.Tab>
              ))
            }
          </CardHeaderNav.Container>
        </CardHeader>

        <CardContent variant="compact">
          <Switch>
            {tabs.map(tab => (
              <Route key={tab.href} path={tab.href} render={tab.render} exact />
            ))}
          </Switch>
        </CardContent>
      </Card>

      <CouponEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        coupon={coupon}
      />
    </>
  );
};

export default DiscountsCouponsDetailOverview;
